import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
} from "reactstrap";
import { useSettlePayoutRequestMutation } from "../../../../redux/services/payOutService";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { Alert } from "react-bootstrap";

const PayoutView: React.FC = () => {
  const location = useLocation();

  const { payoutDetails } = location.state || {};

  const [modalOpen, setModalOpen] = useState(false);

  const [currentStatus, setCurrentStatus] = useState(
    payoutDetails?.status || ""
  );

  const [selectedStatus, setSelectedStatus] = useState<
    "Completed" | "Cancelled" | ""
  >();

  // RTK mutation hook
  const [settlePayoutRequest, { isLoading }] =
    useSettlePayoutRequestMutation();

  const toggleModal = () => setModalOpen(!modalOpen);

  // change state locally
  const ChangeState = async (status: "Completed" | "Cancelled") => {
    setSelectedStatus(status);
    setModalOpen(true);
  };

  const handleSave = async () => {

    if (!selectedStatus) return;

    const requestPayload = {
      payoutRequestId: payoutDetails.id,
      status: selectedStatus,
    };

    try {
      await settlePayoutRequest(requestPayload).unwrap();
      setCurrentStatus(selectedStatus);
      setModalOpen(isLoading);
    } catch (error) {
      console.error("Error occurred while updating payout status:", error);
      setModalOpen(false);
    }
  };

  if (!payoutDetails) {
    return <div><Alert>No payout details available.</Alert></div>;
  }

  const date = new Date(payoutDetails.createdAt._seconds * 1000);
  const formattedDate = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Determine button color based on the current status
  const getStatusButtonColor = () => {
    if (currentStatus === "Cancelled") return "danger";
    if (currentStatus === "Completed") return "success";
    return "info";
  };

  //  Print Payout Details
  const printPayoutDetails = () => {
    const payoutFileName = "Payout_Details";

    const payoutContent = `
    <table>
      <tbody>
        <tr>
          <th>Requested Date</th>
          <td>${formattedDate || "N/A"}</td>
        </tr>
        <tr>
          <th>Requested Type</th>
          <td>${payoutDetails?.requesterType || "N/A"}</td>
        </tr>
        <tr>
          <th>Cart Name</th>
          <td>${payoutDetails?.cart?.displayName || "N/A"}</td>
        </tr>
        <tr>
          <th>Amount</th>
          <td>Rs: ${payoutDetails?.amount?.toFixed(2) || "0.00"}</td>
        </tr>
        <tr>
          <th>Status</th>
          <td>${currentStatus || "N/A"}</td>
        </tr>
        <tr>
          <th colspan="2" style="text-align: center; font-weight: bold;">Bank Details</th>
        </tr>
        <tr>
          <th>Account Number</th>
          <td>${payoutDetails?.userBankDetails?.accountNumber || "N/A"}</td>
        </tr>
        <tr>
          <th>Bank</th>
          <td>${payoutDetails?.userBankDetails?.bankName || "N/A"}</td>
        </tr>
        <tr>
          <th>Bank Branch</th>
          <td>${payoutDetails?.userBankDetails?.branchName || "N/A"}</td>
        </tr>
      </tbody>
    </table>
  `;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(`
      <html>
        <head>
          <title>${payoutFileName}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #000;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid #ccc;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f4f4f4;
            }
            th[colspan="2"] {
              text-align: center;
              font-weight: bold;
              background-color: #eaeaea;
            }
          </style>
        </head>
        <body>
          <h2>${payoutFileName}</h2>
          ${payoutContent}
        </body>
      </html>
    `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <>
      <BreadCrumbs hasSubMenus={false} />



      <Card>
        <CardBody className="m-3">
          <div className="d-flex justify-content-between">
            <h5 className="fw-bold mb-4">Payout Details</h5>

            <Button
              className="px-3"
              size={"md"}
              onClick={printPayoutDetails}
              variant="outline-info"
              style={{
                border: "2px solid #b88017",
                color: "#b88017",
                backgroundColor: "transparent",
              }}
            >
              <FontAwesomeIcon icon={faPrint} title={"Print Promo Codes"} />
            </Button>
          </div>
          <hr />
          <table className="table table-bordered">
            <tbody>
              <tr>
                <th scope="row">Requested Date</th>
                <td>{formattedDate}</td>
              </tr>
              <tr>
                <th scope="row">Requested Type</th>
                <td>{payoutDetails.requesterType}</td>
              </tr>
              <tr>
                <th scope="row">Cart Name</th>
                <td>{payoutDetails.cart?.displayName}</td>
              </tr>
              <tr>
                <th scope="row">Amount</th>
                <td> Rs:{payoutDetails?.amount.toFixed(2)}</td>
              </tr>
              <tr>
                <th scope="row">Status</th>
                <td>
                  <Badge
                    color={getStatusButtonColor()}
                    onClick={toggleModal}
                    className="mx-2 px-3 py-2"
                  >
                    {currentStatus}
                  </Badge>
                </td>
              </tr>
              <tr>
                <th
                  colSpan={2}
                  style={{
                    fontWeight: "bold",
                    textAlign: "center",
                    paddingRight: "600px",
                  }}
                >
                  Bank Details
                </th>
              </tr>
              <tr>
                <th scope="row">Account Number</th>
                <td> {payoutDetails?.userBankDetails.accountNumber}</td>
              </tr>
              <tr>
                <th scope="row">Bank</th>
                <td> {payoutDetails?.userBankDetails.bankName}</td>
              </tr>
              <tr>
                <th scope="row">Bank Branch</th>
                <td> {payoutDetails?.userBankDetails.branchName}</td>
              </tr>
            </tbody>
          </table>

          {currentStatus === "Pending" && (
            <div className="d-flex justify-content-end ">
              <Button
                className="m-3"
                style={{
                  background: "none",
                  color: "#32c232",
                  border: "1px solid #32c232",
                }}
                onClick={() => ChangeState("Completed")}
                disabled={isLoading || currentStatus === "Completed"}
              >
                Mark as Completed
              </Button>
              <Button
                className="m-3"
                style={{
                  background: "none",
                  color: "#dd1714",
                  border: "1px solid #dd1714",
                }}
                onClick={() => ChangeState("Cancelled")}
                disabled={isLoading || currentStatus === "Cancelled"}
              >
                Mark as Cancelled
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Modal UI */}
      <Modal isOpen={modalOpen} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>Are you Sure?</ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-around">
            <Button
              className="px-5"
              style={{
                background: "none",
                color: "#32c232",
                border: "1px solid #32c232",
              }}
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon="spinner" spin color="black" />
                  &nbsp; Saving...
                </>
              ) : (
                "Yes"
              )}
            </Button>
            <Button
              className="px-5"
              style={{
                background: "none",
                color: "#dd1714",
                border: "1px solid #dd1714",
              }}
              onClick={() => setModalOpen(false)}
              disabled={isLoading}
            >
              No
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default PayoutView;
